/**
 * What the recruiter looks at while their board builds.
 *
 * A board build is genuinely slow — a paginated Lever call per open role plus a
 * calendar read per panel — and the first one after an idle period also pays a
 * Cloud Run cold start. The old "Loading…" line gave no sense of whether
 * anything was happening, so people hard-refreshed mid-build and started again
 * from zero.
 *
 * Two deliberate choices:
 *
 *  - The stage line advances on ELAPSED TIME, not on real progress. The board
 *    is a single request, so there is no progress to report; the copy therefore
 *    describes what the server is doing in the order it does it and never
 *    claims a percentage. If it overruns, it says so plainly rather than
 *    sitting on "almost done".
 *  - The Gmail prompt lives HERE. Connecting needs a real click (the popup is
 *    blocked otherwise), it is needed by every send path on the board, and the
 *    wait is the one moment the recruiter has nothing else to do. Asking now
 *    means the first archive or invite of the day does not stop to ask.
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../../shared';

/** Roughly what the server is doing, in the order it does it. */
const STAGES = [
  { after: 0, label: 'Waking the board service…' },
  { after: 3, label: 'Reading your open roles from Lever…' },
  { after: 8, label: 'Pulling candidates for each role…' },
  { after: 18, label: 'Checking interviewer calendars…' },
  { after: 30, label: 'Working out what needs your attention…' },
  {
    after: 45,
    label: 'Still going — a first load with many open roles can take a while.',
  },
];

export function BoardLoadingState({
  firstName,
  gmailConnected,
  gmailConnecting,
  onConnectGmail,
}: {
  firstName: string;
  gmailConnected: boolean;
  gmailConnecting: boolean;
  onConnectGmail: () => void;
}) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const stage = [...STAGES].reverse().find((s) => elapsed >= s.after) ?? STAGES[0];

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600"
            aria-hidden
          />
          <p className="text-sm font-semibold text-gray-900">
            Building {firstName ? `${firstName}’s` : 'the'} action board
          </p>
          <span className="text-xs tabular-nums text-gray-400">{elapsed}s</span>
        </div>
        <p aria-live="polite" className="mt-2 text-sm text-gray-600">
          {stage.label}
        </p>
        <p className="mt-1 text-xs text-gray-400">
          It stays put while it loads — refreshing starts the whole build again.
        </p>
      </div>

      {!gmailConnected && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-5">
          <p className="text-sm font-semibold text-gray-900">
            Connect Gmail while you wait
          </p>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Invitations, slot proposals and rejection emails all go out from your own mailbox, so
            the board needs your permission once per session. Doing it now means the first email
            you send today goes straight out instead of stopping to ask.
          </p>
          <Button size="sm" className="mt-3" loading={gmailConnecting} onClick={onConnectGmail}>
            Connect Gmail
          </Button>
        </div>
      )}

      {/* A rough silhouette of the board, so the page has the shape of the thing
          that is coming rather than jumping into existence at the end. */}
      <div className="space-y-3" aria-hidden>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              'animate-pulse rounded-xl border border-gray-200 bg-white p-4',
              i === 0 ? 'opacity-100' : i === 1 ? 'opacity-70' : 'opacity-40'
            )}
          >
            <div className="h-4 w-1/3 rounded bg-gray-200" />
            <div className="mt-3 space-y-2">
              <div className="h-3 w-2/3 rounded bg-gray-100" />
              <div className="h-3 w-1/2 rounded bg-gray-100" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
